import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import facturaVentaService, { FacturaVenta } from '../../services/facturaVentaService';
import notaCreditoVentaService from '../../services/notaCreditoVentaService';
import { facturaToNotaCreditoFormState } from '../../utils/facturaToNotaCredito';
import { getApiErrorMessage } from '../../utils/apiError';
import type { NotaCreditoFormState } from './NotaCreditoForm';
import NotaCreditoForm from './NotaCreditoForm';

const NuevaNotaCredito: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const facturaIdParam = searchParams.get('facturaId');
  const facturaId = facturaIdParam ? parseInt(facturaIdParam, 10) : null;

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [formState, setFormState] = useState<NotaCreditoFormState | null>(null);
  const [loadingFactura, setLoadingFactura] = useState(false);
  const [errorFactura, setErrorFactura] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [selectorOpen, setSelectorOpen] = useState(false);
  const [facturas, setFacturas] = useState<FacturaVenta[]>([]);
  const [loadingFacturas, setLoadingFacturas] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLetra, setSearchLetra] = useState('');
  const [searchNroSuc, setSearchNroSuc] = useState<string>('');
  const [searchNroComprobante, setSearchNroComprobante] = useState<string>('');
  const [searchNroCliente, setSearchNroCliente] = useState<string>('');
  const [fechaDesde, setFechaDesde] = useState<string>(() =>
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
  );
  const [fechaHasta, setFechaHasta] = useState<string>(() =>
    new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]
  );
  const [totalFacturas, setTotalFacturas] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const loadFacturaAndSetForm = async (nroFormulario: number) => {
    setLoadingFactura(true);
    setErrorFactura(null);
    try {
      const data = await facturaVentaService.getFacturaVenta(nroFormulario);
      const f = data as any;
      const facturaWithExt = {
        ...f,
        ordenCompra: f.ordenDeCompra ?? f.ordenCompra ?? '',
        formaDePago: f.formaDePago ?? '',
        nroUbicacionOrigen: f.nroUbicacionOrigen,
        formaEntrega: f.formaEntrega ?? f.transporte,
      };
      const state = facturaToNotaCreditoFormState(facturaWithExt);
      if (!state.formulario.idCompania) {
        state.formulario.idCompania = '1';
      }
      setFormState(state);
      setSelectorOpen(false);
    } catch (err: any) {
      const msg = getApiErrorMessage(err, 'Error al cargar la factura');
      setErrorFactura(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoadingFactura(false);
    }
  };

  useEffect(() => {
    if (!facturaId || isNaN(facturaId)) return;
    loadFacturaAndSetForm(facturaId);
  }, [facturaId]);

  const handleSelectFactura = (nroFormulario: number) => {
    setSearchParams({ facturaId: String(nroFormulario) });
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (!selectorOpen) return;
    setPage(0);
  }, [selectorOpen]);

  useEffect(() => {
    if (!selectorOpen) return;
    setPage(0);
  }, [searchTerm, searchLetra, searchNroSuc, searchNroComprobante, searchNroCliente, fechaDesde, fechaHasta]);

  useEffect(() => {
    if (!selectorOpen) return;
    let cancelled = false;
    setLoadingFacturas(true);
    const nroSucNum = searchNroSuc.trim() ? parseInt(searchNroSuc, 10) : undefined;
    const nroCompNum = searchNroComprobante.trim() ? parseInt(searchNroComprobante, 10) : undefined;
    const nroClienteNum = searchNroCliente.trim() ? parseInt(searchNroCliente, 10) : undefined;
    facturaVentaService
      .getFacturasVenta({
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        searchTerm: searchTerm || undefined,
        letra: searchLetra.trim() || undefined,
        nroSuc: Number.isFinite(nroSucNum) ? nroSucNum : undefined,
        nroComprobante: Number.isFinite(nroCompNum) ? nroCompNum : undefined,
        nroCliente: Number.isFinite(nroClienteNum) ? nroClienteNum : undefined,
        fechaDesde: fechaDesde ? new Date(fechaDesde) : undefined,
        fechaHasta: fechaHasta ? new Date(fechaHasta) : undefined,
      })
      .then((res) => {
        if (!cancelled) {
          setFacturas(res.items || []);
          setTotalFacturas(res.totalCount ?? 0);
        }
      })
      .catch(() => {
        if (!cancelled) setFacturas([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingFacturas(false);
      });
    return () => { cancelled = true; };
  }, [selectorOpen, searchTerm, searchLetra, searchNroSuc, searchNroComprobante, searchNroCliente, fechaDesde, fechaHasta, page, rowsPerPage]);

  const handleSave = async (payload: Parameters<typeof notaCreditoVentaService.createNotaCredito>[0]) => {
    setSaving(true);
    try {
      await notaCreditoVentaService.createNotaCredito(payload);
      enqueueSnackbar('Nota de crédito registrada correctamente', { variant: 'success' });
      navigate('/ventas/notas-credito');
    } catch (err: any) {
      const msg = getApiErrorMessage(err, 'Error al guardar la nota de crédito');
      enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d: string | Date | undefined) => {
    if (!d) return '-';
    try {
      const date = typeof d === 'string' ? new Date(d) : d;
      return date.toLocaleDateString('es-AR');
    } catch {
      return '-';
    }
  };

  const formatCurrency = (n: number | undefined) => {
    if (n === undefined || n === null) return '-';
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 }).format(n);
  };

  const formatComprobante = (f: FacturaVenta) => {
    const letra = f.letra?.trim().toUpperCase() || '';
    const suc = f.nroSuc != null ? String(f.nroSuc).padStart(5, '0') : '';
    const comp = f.nroComprobante != null ? String(f.nroComprobante).padStart(8, '0') : '';
    if (!letra && !suc && !comp) return '-';
    return `${letra || '-'}-${suc || '-'}-${comp || '-'}`;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <IconButton
              onClick={() =>
                window.history.length > 1 ? navigate(-1) : navigate('/ventas/notas-credito')
              }
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Nueva Nota de Crédito de Venta
            </Typography>
          </Box>

          {!facturaId && !formState && (
            <Box>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Seleccione la factura de venta sobre la cual desea emitir la nota de crédito.
              </Typography>
              <Button variant="contained" color="primary" onClick={() => setSelectorOpen(true)}>
                Seleccionar factura
              </Button>
            </Box>
          )}

          {facturaId && loadingFactura && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={24} />
              <Typography>Cargando factura...</Typography>
            </Box>
          )}

          {facturaId && errorFactura && !loadingFactura && (
            <Alert severity="error" sx={{ mt: 2 }} action={<Button color="inherit" size="small" onClick={() => navigate('/ventas/notas-credito')}>Volver</Button>}>
              {errorFactura}
            </Alert>
          )}

          {formState && !loadingFactura && (
            <NotaCreditoForm
              key={formState.formulario.nroFormularioFactura ?? 0}
              initialState={formState}
              onSave={handleSave}
              loading={saving}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={selectorOpen} onClose={() => setSelectorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Seleccionar factura de venta</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Buscar por texto (cliente, nombre, letra, punto venta, nro comprobante...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 1, mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              label="Letra"
              placeholder="Ej. A, B"
              value={searchLetra}
              onChange={(e) => setSearchLetra(e.target.value)}
              sx={{ minWidth: 80 }}
              inputProps={{ maxLength: 2 }}
            />
            <TextField
              size="small"
              label="Pto. venta / Nro Suc"
              placeholder="Ej. 1"
              value={searchNroSuc}
              onChange={(e) => setSearchNroSuc(e.target.value.replace(/\D/g, ''))}
              sx={{ minWidth: 100 }}
            />
            <TextField
              size="small"
              label="Nro Comprobante"
              placeholder="Ej. 123"
              value={searchNroComprobante}
              onChange={(e) => setSearchNroComprobante(e.target.value.replace(/\D/g, ''))}
              sx={{ minWidth: 120 }}
            />
            <TextField
              size="small"
              label="Nro. Cliente"
              placeholder="Ej. 1001"
              value={searchNroCliente}
              onChange={(e) => setSearchNroCliente(e.target.value.replace(/\D/g, ''))}
              sx={{ minWidth: 120 }}
            />
            <TextField
              size="small"
              label="Fecha desde"
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 140 }}
            />
            <TextField
              size="small"
              label="Fecha hasta"
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 140 }}
            />
          </Box>
          {loadingFacturas ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nro. interno</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Nro. Comprobante</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="center">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {facturas.map((f) => (
                    <TableRow key={f.nroFormulario}>
                      <TableCell>{f.nroFormulario}</TableCell>
                      <TableCell>{formatDate(f.fechaEmision)}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{formatComprobante(f)}</TableCell>
                      <TableCell>{f.nroCliente ?? '-'}</TableCell>
                      <TableCell>{f.nombre?.trim() || '-'}</TableCell>
                      <TableCell align="right">{formatCurrency(f.importeTotal)}</TableCell>
                      <TableCell align="center">
                        <Button size="small" variant="outlined" onClick={() => handleSelectFactura(f.nroFormulario)}>
                          SELECCIONAR
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalFacturas}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
                labelRowsPerPage="Filas:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
              />
            </TableContainer>
          )}
          {!loadingFacturas && facturas.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No hay facturas para mostrar.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default NuevaNotaCredito;
